import { PersonSet } from '../../collection/sets/person_set';
import { ResourceSet } from '../../collection/sets/resource_set';
import { SlotSet } from '../../collection/sets/slot_set';
import { BookingSet } from '../../collection/sets/booking_set';
import { ServiceSet } from '../../collection/sets/service_set';
import { OrderSet } from '../../collection/sets/order_set';
import { CompanyModel } from '../core/company';
import { adminCompany } from '../../services/admin_company';
import { ClientSet } from '../../collection/sets/client_set';
import { ClientQueueSet } from '../../collection/sets/client_queue_set';
import { EventChainSet } from '../../collection/sets/event_chain_set';
import { BBModel } from '../../bb_model';
import { halClient } from '../../hal';
import { context } from "../../constants/context";
import { currentCompanyTracker } from '../../services/current_company_tracker';

class AdminCompanyModel extends CompanyModel {

    constructor(data) {
        super(data);
        this.isPostCreationInitialised = false;
    }

    ignoreKeys() {
        return super.ignoreKeys().concat([
            'isPostCreationInitialised',
            'services',
            'people',
            'resources',
            'bookings',
            'slots',
            'orders',
            'clients',
            'client_queues',
            'child_companies',
            'clientPusherChannelPromise',
            'liveStatus',
            'pusherChannelPromise',
            'pusherConnection',
            'pusher_channel'
        ]);
    }

    async initLiveStatus() {
        await currentCompanyTracker.initLiveStatus(this);
    }

    async postCreationInitialisation(track_updates = true) {
        try {
            if (this.isPostCreationInitialised) return;

            this.isPostCreationInitialised = true;

            if (this.$has('settings')) {
                this.$get('settings').then(settings => this.settings = settings);
            }

            if (track_updates) {
                await currentCompanyTracker.trackCompany(this);
            }

            // If adding something new here, also update models array in flushCache.
            this.services = new ServiceSet(this);
            this.people = new PersonSet(this);
            this.resources = new ResourceSet(this);
            this.bookings = new BookingSet(this);
            this.slots = new SlotSet(this);
            this.orders = new OrderSet(this);
            this.clients = new ClientSet(this);
            this.client_queues = new ClientQueueSet(this);
            this.event_chains = new EventChainSet(this);
            AdminCompanyModel.all[this.id] = this;
        } catch (err) {
            console.error(err);
        }
    }

    resetInitialisation() {
        this.isPostCreationInitialised = false;
    }

    flushCache() {
        if (this.isPostCreationInitialised) {
            const models = [
                'services',
                'people',
                'resources',
                'bookings',
                'slots',
                'orders',
                'clients',
                'client_queues',
                'event_chains'
            ];

            models.forEach((model) => {
                if (this[model]) {
                    this[model].clearData();
                }
            });
        }
    }

    _setChildCompanies() {

        if (!this.companies) return;

        /**
         * @type {Array<BBModel.Admin.Company>} Direct ancestors
         */
        this.child_companies = [];

        for (const childCompanyRaw of Array.from(this.companies)) {
            const childCompany = new BBModel.Admin.Company(halClient.$parse(childCompanyRaw));
            this.child_companies.push(childCompany);
        }

        this._setAllCompanies();
    }

    $getBooking(id) {
        return BookingSet.getBooking(this, id);
    }

    static $query(params) {
        return adminCompany.query(params);
    }

    $getBookings(params) {
        return BookingSet.$query(this, params);
    }

    $getSlots(params) {
        return SlotSet.$query(this, params);
    }

    $getPeople() {
        return PersonSet.$query(this);
    }

    $getPerson(id) {
        return PersonSet.$getPerson(this, id);
    }

    $getResources() {
        return ResourceSet.$query(this);
    }

    $getResource(id) {
        return ResourceSet.$getResource(this, id);
    }

    $getServices() {
        return ServiceSet.$query(this);
    }

    $getService(id) {
        return ServiceSet.$getService(this, id);
    }

    $getOrders(params) {
        return this.orders.$query(this, params);
    }

    $getOrder(id) {
        return this.orders.getOrder(this, id);
    }

    $getClients(params) {
        return ClientSet.$query(this, params);
    }

    $getClient(id) {
        return this.clients.getClient(this, id);
    }

    $getClientQueues() {
        return ClientQueueSet.$query(this);
    }

    $getClientQueue(id) {
        return ClientQueueSet.$getClientQueue(this, id);
    }

    $getEventChains(params) {
        return this.event_chains.$query(this, params);
    }

    $getEventChain(id, params) {
        return this.event_chains.getEventChain(this, id, params);
    }

    getApp(name){
        return new Promise((resolve, reject) => {
            this.$get('apps', {app_name: name}).then( (app) => {
                return resolve(new BBModel.Admin.JRNIApp(app));
            }, (err) => {
                return reject(err);
            });
        });
    }

    $getAdministrator(id) {
        return new Promise((resolve, reject) => {
            this.$get('administrators', {id}).then((administrator) => {
                return resolve(new BBModel.Admin.User(administrator));
            }
            , (err) => {
                console.error(err);
                return reject(err);
            });
        });
    }

    // get the current live status - assuming we're tracking live status for this company
    get liveStatus() {
        if (this.isTracked) {
            return currentCompanyTracker.liveStatus;
        } else {
            return null;
        }
    }

    // get the pusher channel for this company
    get pusherChannelPromise() {
        if (this.isTracked){
            return currentCompanyTracker.pusherChannelPromise;
        } else {
            return null;
        }
    }

    // get the pusher connection for this company
    get pusherConnection() {
        if (this.isTracked){
            return currentCompanyTracker.pusherConnection;
        } else {
            return null;
        }
    }

    // find out if the current company is being tracked for live updated
    get isTracked() {
        return (currentCompanyTracker.company && currentCompanyTracker.company.id === this.id);
    }



}

AdminCompanyModel.all = {};



export {
    AdminCompanyModel
};
